import React from 'react';
import styled from 'styled-components';
import { CHANGE_GLYPH, Icon, Note, type IconName, type NoteProps } from '@repo/design-system';
import { semantic, type StatusRole } from '@repo/design-tokens';

const { roles } = semantic.status;

/** A role Launch can draw as a `Note`: every role whose `noteTone` is not `null`. */
export type LaunchRole = {
  [R in StatusRole]: (typeof roles)[R]['noteTone'] extends null ? never : R;
}[StatusRole];

/**
 * Every flag the Launch step draws, keyed by name, and the status role it takes
 * (`onboarding-status-colours-launch`, R1). Roles, never hues: the colour is the role's, from Rob's
 * status page, so one meaning keeps one colour across the wizard. A role with no `Note` tone (all
 * done, dead end) is a type error here, so no edit can reach a tone that is not drawn.
 */
export const LAUNCH_FLAG_ROLE = {
  /** The register is read and empty. */
  equipmentSkipped: 'problem',
  /** Twin details: the position is the old placeholder default. */
  placeholderPosition: 'problem',
  /** Twin details: no position set. */
  noPosition: 'stopsGoLive',
  /** Opening hours: no open day, no timezone or no standard hours. */
  hoursHeld: 'stopsGoLive',
  /** Opening hours: they start later. */
  hoursUpcoming: 'information',
  /** Equipment: assets not in the catalogue, none unknown. */
  assetsUncatalogued: 'workToDo',
  /** Equipment: any unknown asset. */
  assetsUnknown: 'problem',
  /** BI hierarchies: a placed item with no catalogue reference. */
  biNoReference: 'problem',
  /** BI hierarchies: gaps in BI's catalogue only. */
  biGapsOnly: 'information',
  /** Floor Plan: register items not yet placed. */
  unplaced: 'problem',
  /** Camera Feeds: any camera not linked. */
  camerasUnlinked: 'workToDo',
  /** Observers: zones or equipment without an observer. */
  withoutObserver: 'problem',
  /** Measurement coverage: tracked by neither an observer nor a composite measure. */
  untracked: 'problem',
  /** Composite measures that would be refused, and the go-live held line. */
  refusedComposites: 'stopsGoLive',
  /** Composite measures that add up nothing. */
  emptyComposites: 'workToDo',
  /** Composite measures not finished. */
  unfinishedComposites: 'workToDo',
  /** Composite measures with nothing measured. */
  nothingMeasured: 'workToDo',
  /** Generated totals that need review. */
  generatedReview: 'problem',
} as const satisfies Record<string, LaunchRole>;

export type LaunchFlag = keyof typeof LAUNCH_FLAG_ROLE;
type FlagRole = (typeof LAUNCH_FLAG_ROLE)[LaunchFlag];

/**
 * The `Note` glyph per role, each already used for that meaning in the wizard: the error notes'
 * `circle-exclamation`, the warning box's `triangle-exclamation` it replaces.
 */
export const LAUNCH_ROLE_GLYPH = {
  stopsGoLive: 'circle-exclamation',
  problem: 'triangle-exclamation',
  workToDo: 'list-check',
  information: 'circle-info',
} as const satisfies Record<FlagRole, IconName>;

/** Strongest first: where one sentence carries two flags it takes the stronger (R2). */
const ORDER: readonly FlagRole[] = ['stopsGoLive', 'problem', 'workToDo', 'information'];

/** The strongest of the roles given, or `null` for none. */
export const strongestRole = (roles: readonly FlagRole[]): FlagRole | null =>
  ORDER.find(role => roles.includes(role)) ?? null;

/** The raised flag whose role is strongest, or `null` when none is raised. */
export const strongestFlag = (
  raised: readonly (LaunchFlag | false | null | undefined)[],
): LaunchFlag | null => {
  const flags = raised.filter((flag): flag is LaunchFlag => !!flag);
  const role = strongestRole(flags.map(flag => LAUNCH_FLAG_ROLE[flag]));
  return flags.find(flag => LAUNCH_FLAG_ROLE[flag] === role) ?? null;
};

type LaunchFlagNoteProps = Omit<NoteProps, 'tone' | 'bare' | 'icon'> & { flag: LaunchFlag };

/**
 * One raised Launch flag: a bare design-system `Note` in its role's tone, with its role's glyph.
 * The sentence is the caller's, word for word; `id` and `role` pass through to the element holding it.
 */
export const LaunchFlagNote: React.FC<LaunchFlagNoteProps> = ({ flag, children, ...rest }) => {
  const role = LAUNCH_FLAG_ROLE[flag];
  return (
    <Note
      bare
      tone={roles[role].noteTone}
      icon={<Icon name={LAUNCH_ROLE_GLYPH[role]} size="xs" />}
      {...rest}
    >
      {children}
    </Note>
  );
};

/**
 * Flagged or plain: the sentence in its flag's `Note` when one is raised, else in today's `<p>`.
 * A flag never stands on a read that has not answered, so callers pass `null` for any state but
 * `ready`.
 */
export const LaunchSentence: React.FC<{
  flag: LaunchFlag | null;
  children: React.ReactNode;
}> = ({ flag, children }) =>
  flag ? <LaunchFlagNote flag={flag}>{children}</LaunchFlagNote> : <p>{children}</p>;

/**
 * The dead-end colour around a glyph (`onboarding-status-colours-launch`, R5): something that cannot
 * be undone after launch. Dead end has no `Note` or `Tag` tone, so it is a glyph in its colour beside
 * the words that say it (Rob, 2026-10-28). Decorative: the words carry the meaning.
 */
export const DeadEndMark = styled.span.attrs({ 'aria-hidden': true, className: 'dead-end' })`
  display: inline-flex;
  vertical-align: middle;
  color: ${roles.deadEnd.mark};
`;

/** The removed glyph in the dead-end colour, at the start of a dead-end sentence or heading. */
export const DeadEndGlyph: React.FC = () => (
  <DeadEndMark>
    <Icon name={CHANGE_GLYPH.removed} size="sm" />
  </DeadEndMark>
);
